import { z } from 'zod';

import { ContractType, HiredDateLimitKind } from '@/common/graphql';

export const TABULATION_MIN_MONTHS_BEFORE = 0;

/** Shape of the hire-date limit without the cross-field rules, so a company whose switch is off is not validated. */
const tabulationHiredDateLimitShape = z.object({
  kind: z.nativeEnum(HiredDateLimitKind),
  monthsBefore: z.number().nullable(),
  date: z.string().nullable(),
});

export const tabulationHiredDateLimitSchema = tabulationHiredDateLimitShape.superRefine((value, ctx) => {
  if (value.kind === HiredDateLimitKind.Relative) {
    const { monthsBefore } = value;
    if (monthsBefore === null || !Number.isInteger(monthsBefore) || monthsBefore < TABULATION_MIN_MONTHS_BEFORE) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Enter a whole number of ${TABULATION_MIN_MONTHS_BEFORE} or more`,
        path: ['monthsBefore'],
      });
    }
  }
  if (value.kind === HiredDateLimitKind.Absolute && !value.date) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Select a date', path: ['date'] });
  }
});

export type TabulationHiredDateLimitFormData = z.infer<typeof tabulationHiredDateLimitSchema>;

export const tabulationRuleSchema = z
  .object({
    // An empty list is valid: it excludes the country or company from tabulation for the period.
    contractTypes: z.array(z.nativeEnum(ContractType)),
    hiredDateLimit: tabulationHiredDateLimitShape,
  })
  .superRefine((value, ctx) => {
    // The limit is hidden and not sent while no contract type is selected, so a leftover value cannot block Save.
    if (value.contractTypes.length === 0) return;
    const result = tabulationHiredDateLimitSchema.safeParse(value.hiredDateLimit);
    if (result.success) return;
    for (const issue of result.error.issues) {
      ctx.addIssue({ ...issue, path: ['hiredDateLimit', ...issue.path] });
    }
  });

export type TabulationRuleFormData = z.infer<typeof tabulationRuleSchema>;

/** A rule with nothing selected yet: the starting point for a country without a stored row. */
export const emptyTabulationRule = (): TabulationRuleFormData => ({
  contractTypes: [],
  hiredDateLimit: { kind: HiredDateLimitKind.None, monthsBefore: null, date: null },
});

/**
 * A company-specific rule is only validated while its switch is on, so the stored shape is the
 * lax one and the full rule is checked in `superRefine`, keeping the issue paths of the full rule.
 */
export const tabulationCompanySchema = z
  .object({
    companyId: z.string(),
    enabled: z.boolean(),
    rule: z.object({
      contractTypes: z.array(z.nativeEnum(ContractType)),
      hiredDateLimit: tabulationHiredDateLimitShape,
    }),
  })
  .superRefine((value, ctx) => {
    if (!value.enabled) return;
    const result = tabulationRuleSchema.safeParse(value.rule);
    if (result.success) return;
    for (const issue of result.error.issues) {
      ctx.addIssue({ ...issue, path: ['rule', ...issue.path] });
    }
  });

export const tabulationCountrySchema = z.object({
  countryId: z.string(),
  rule: tabulationRuleSchema,
  companies: z.array(tabulationCompanySchema),
});

export const tabulationFormSchema = z.object({
  countries: z.array(tabulationCountrySchema),
});

export type TabulationFormData = z.infer<typeof tabulationFormSchema>;
